import type { FC, PropsWithChildren } from "react";
import React, { forwardRef, useEffect } from "react";

import { useFeatureState } from "./FeaturesProvider";
import { fetchEvaluateFeature, parsePartition } from "./utils";

type FeatureVariations<TProps> =
  | Array<FC<TProps> | null>
  | ((patition: number) => FC<TProps>);

interface CreateFeatureArgs<TProps> {
  name: string;
  variations: FeatureVariations<TProps>;
  version?: 1 | 2;
  LoadingComponent?: FC<TProps>;
}

const EmptyComponent: FC = () => null;

function getVariationComponent<TProps>({
  variations,
  index,
  version,
}: {
  variations: FeatureVariations<TProps>;
  index: number | undefined;
  version: number;
}): FC<TProps> | FC {
  if (typeof variations === "function") {
    /* TODO: review this implementation. Really should receive undefined? Maybe should receive the correct parsePartition but this will be a breaking change. */
    return variations(index as number) || EmptyComponent;
  }

  const parsedPartition = parsePartition({ variations, index, version });
  return variations[parsedPartition] || EmptyComponent;
}

export function createFeature<TProps>({
  name,
  variations,
  LoadingComponent,
  version,
}: CreateFeatureArgs<TProps>) {
  return forwardRef<unknown, PropsWithChildren<TProps>>((props, ref) => {
    const { status, index, evaluable, context, evaluatedFeatures } =
      useFeatureState(name);

    useEffect(() => {
      if (
        status === "success" &&
        version !== 1 &&
        !evaluatedFeatures.current.has(name)
      ) {
        evaluatedFeatures.current.add(name);
        fetchEvaluateFeature({ context, name, index, evaluable: !!evaluable });
      }
    }, [status, evaluable, index, context, evaluatedFeatures]);

    let Component =
      (typeof variations !== "function" && variations[0]) || EmptyComponent;
    if (status === "success") {
      Component = getVariationComponent<TProps>({
        variations,
        index,
        version: version ?? 2,
      });
    } else if (status === "loading" && LoadingComponent) {
      Component = LoadingComponent;
    }

    return <Component ref={ref} {...(props as TProps)} />;
  });
}
